import { ClickAwayListener, Popper } from "@mui/base";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  cssFontFamily,
  cssFontFamilyList,
  parseCssFontFamilyList,
} from "@redotech/redo-content-builder-common/brand-kit/font-utils";
import {
  applyDynamicVariableSyntaxLiquid,
  DynamicVariable,
  SchemaType,
} from "@redotech/redo-flows-common/schemas/schemas";
import { SchemaField } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { repeat } from "@redotech/util/array";
import { sanitizeUrl } from "@redotech/util/link";
import { assertNever, Value } from "@redotech/util/type";
import ResizeModule from "@ssumo/quill-resize-module";
import classNames from "classnames";
import Quill from "quill";
import MagicUrl from "quill-magic-url";
import { Delta, Parchment, Range } from "quill/core";
import Emitter from "quill/core/emitter";
import "quill/dist/quill.snow.css";
import * as React from "react";
import {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RedoColorPicker } from "../arbiter-components/color-picker/redo-color-picker";
import { RedoList, RedoListItem } from "../arbiter-components/list/redo-list";
import { Dropdown } from "../dropdown";
import { DynamicVariableSelectionModal } from "../dynamic-variable-selection-modal";
import {
  BaseEmoji,
  CustomEmoji,
  EmojiSelectionModal,
  EmojiSelectionModalCloseCause,
} from "../emoji-selection-modal";
import { Flex } from "../flex";
import { InlineVariableMenu } from "../inline-variable-menu";
import { Text } from "../text";
import { ThemeContext } from "../theme-provider";
import { useInlineVariableAutocomplete } from "../use-inline-variable-autocomplete";
import { FontFamilyOption, partitionBrandFonts } from "./font-family-options";
import { FormQuillEditorToolbar } from "./form-quill-editor-toolbar";
import {
  IMAGE_LAYOUT_STYLE_FORMATS,
  QuillClipboardMatcher,
  quillClipboardMatchers,
} from "./matchers";
import * as quillEditorCss from "./quill-editor.module.css";
import { applyFontSize, readFontSize, stepFontSize } from "./quill-font-size";
import { QuillLink } from "./quill-link";
import { QuillLinkPopup } from "./quill-link-popup";

export const quillFakeCursorPadding = 2;

export type OnEditorChangeArgs =
  | [typeof Quill.events.TEXT_CHANGE, Delta, Delta, string]
  | [typeof Quill.events.SELECTION_CHANGE, Range, Range, string];

/**
 * The element a toolbar popup should hang off. The toolbar collapses overflowing
 * controls into a menu that leaves the real button `display: none`, and a hidden
 * element has no bounding rect, so anchoring to it drops the popup in the page
 * corner. Fall back to the overflow trigger, then the bar itself.
 */
function visibleToolbarAnchor(
  button: HTMLElement | null,
  toolbarId: string | undefined,
): HTMLElement | null {
  if (button?.offsetParent || !toolbarId) {
    return button;
  }
  const toolbar = document.getElementById(toolbarId);
  return (
    toolbar?.querySelector<HTMLElement>(
      '[data-testid="quill-toolbar-overflow-menu"]',
    ) ?? toolbar
  );
}

/** Empty id = "no per-selection family", which clears the format. */
const FONT_FAMILY_INHERIT_ID = "";

const FontFamilyList = memo(function FontFamilyList({
  options,
  value,
  inheritLabel,
  onSelect,
}: {
  options: readonly FontFamilyOption[];
  value: string | undefined;
  inheritLabel: string;
  onSelect: (fontFamily: string) => void;
}) {
  const [focusedIndex, setFocusedIndex] = useState<number | undefined>();

  const items = useMemo<RedoListItem<string>[]>(() => {
    // The name renders in the UI face so display fonts without lowercase
    // glyphs stay readable; the sample carries the preview.
    const toItem = (option: FontFamilyOption): RedoListItem<string> => ({
      id: option.value,
      value: option.value,
      type: "custom",
      useWrapper: true,
      render: (
        <Flex align="center" gap="md" justify="space-between" w="full">
          <Text fontSize="sm">{option.label}</Text>
          <Text
            fontSize="sm"
            //redontIgnore -- per-option brand family is dynamic; no Arbiter prop or CSS module can carry it
            style={{ fontFamily: cssFontFamily(option.value) }}
            textColor="secondary"
          >
            AaBb
          </Text>
        </Flex>
      ),
    });

    const { brand, system } = partitionBrandFonts(options);
    const section = (id: string, text: string): RedoListItem<string> => ({
      id,
      type: "section",
      text,
      value: FONT_FAMILY_INHERIT_ID,
    });

    return [
      {
        id: FONT_FAMILY_INHERIT_ID,
        type: "text",
        text: inheritLabel,
        value: FONT_FAMILY_INHERIT_ID,
      },
      ...(brand.length
        ? [
            section("__brand__", "Brand fonts"),
            ...brand.map(toItem),
            section("__system__", "System fonts"),
          ]
        : []),
      ...system.map(toItem),
    ];
  }, [options, inheritLabel]);

  // RedoList's overflow-hidden wrapper shrinks to fit the capped Dropdown and
  // clips; shrink=0 keeps natural height so the Dropdown scrolls instead.
  return (
    <Flex dir="column" shrink={0}>
      <RedoList
        focusedIndex={focusedIndex}
        items={items}
        itemSelected={({ item }) => onSelect(item.value)}
        refToListenTo={null}
        selectedItems={value ?? FONT_FAMILY_INHERIT_ID}
        setFocusedIndex={setFocusedIndex}
        size="sm"
      />
    </Flex>
  );
});

export interface QuillEditorProps {
  /** The toolbar element's id. */
  toolbarId?: string;
  /** Refers to the common toolbar defined within QuillEditor.
   * If using a custom toolbar, you can ignore this prop. */
  commonToolbar?: {
    toolbarOpen: boolean;
    /** Show the per-line heading toggle. */
    includeHeading?: boolean;
    /**
     * Show a per-selection font picker offering these families. Opt-in: the
     * surface has to render the resulting inline `font-family` and load the
     * face, or a merchant picks a font that never shows up. Editors without
     * this list also strip `font-family` from pasted content, so the globally
     * registered format leaves their behavior unchanged.
     *
     * Must be non-empty from the first render. The control is a plain button
     * this component binds by querying the toolbar DOM once, alongside Quill's
     * own setup, so a list that arrives later gets no click handler.
     */
    fontFamilies?: readonly FontFamilyOption[];
    /** Label for the entry that clears the per-selection family. */
    fontFamilyInheritLabel?: string;
    /** Defaults on; opt out where content should not carry links. */
    includeLink?: boolean;
    /** Defaults on; opt out where an emoji picker is noise. */
    includeEmoji?: boolean;
    /** In-flow with overflow collapse; off by default so un-anchored bars float. */
    inline?: boolean;
    /** When provided, the toolbar floats above this element and tracks it through scroll. */
    anchorEl?: HTMLElement | null;
    /** Legacy static-rect alternative to anchorEl (the caller tracks it). */
    anchorRect?: DOMRect | null;
    /** When true, renders toolbar via portal (must be consistent to preserve Quill bindings) */
    usePortal?: boolean;
  };

  readOnly?: boolean;
  defaultHtmlContents?: string;

  /**
   * A hack to get around quill's internal state. This is used to
   * force quill to re-render the editor when the default html contents
   * change. This is a workaround for quill not re-rendering
   */
  defaultHtmlContentOverridesInternalState?: boolean;

  /** Called when the text changes. https://quilljs.com/docs/api#text-change */
  onTextChange?: () => void;

  /** Called when the selection changes. https://quilljs.com/docs/api#selection-change */
  onSelectionChange?: (
    range: { index: number; length: number } | null,
    oldRange: { index: number; length: number } | null,
    source: string,
  ) => void;

  /** Called when the selection or text changes, even if they changed silently,
   * as in the case of the selection-change event fired after a text-change.
   * https://quilljs.com/docs/api#editor-change */
  onEditorChange?: (args: OnEditorChangeArgs) => void;

  placeholder?: string;
  onQuillEditorEmptyChange?: (empty: boolean) => void;

  cursorIndexRef?: React.MutableRefObject<number | undefined>;
  setEmojiPickerOpenRef?: React.MutableRefObject<
    React.Dispatch<React.SetStateAction<boolean>> | undefined
  >;

  /** HTML element to anchor the emoji picker to. If not provided, the emoji picker will be anchored
   * to the cursor if opened via hotkey and to the emoji button if opened via click.
   */
  emojiPickerAnchorOverride?: HTMLElement | null;

  /**
   * To make font size portable, you may want the output of the editor to be inline styles instead of classes.
   * Everything except the settings -> email builder's text field works this way.
   */
  inlineFontSizeStyles?: boolean;

  /**
   * Class name to apply to the editor container.
   */
  editorClassName?: string;

  /**
   * Styles to apply to the editor container.
   */
  editorStyles?: CSSProperties & { fontSize?: number | undefined };

  /**
   * Id to apply to the editor container. Useful for CSS targeting.
   */
  editorId?: string;

  /**
   * Allow pasting images and videos.
   */
  allowEmbeds?: boolean;

  /**
   * Whitelisted font families.
   */
  fontFamilies?: readonly string[];

  /**
   * Available dynamic variables.
   */
  dynamicVariables?: readonly SchemaField[];

  /**
   * Schema type for automatic filter detection.
   */
  schemaType?: SchemaType;

  /**
   * When true, typing `{{` or `@` opens an inline dropdown to pick a dynamic
   * variable, in addition to the toolbar button. Requires `dynamicVariables`.
   */
  enableInlineVariableAutocomplete?: boolean;

  /**
   * quill-magic-url's automatic URL detection/normalization. Defaults to on.
   * Set false for editors whose links are raw-text `{{ … }}` variable tokens
   * (e.g. table cells), where magic-url's normalization would prepend a protocol
   * to the token and corrupt the link on re-process.
   */
  enableMagicUrl?: boolean;

  /**
   * Default styling applied as real Quill formats (not CSS) when a link is
   * created, so links look like links by default while staying editable: the
   * underline button reflects the state and the color picker reads the color.
   */
  defaultLinkStyle?: { color?: string; underline?: boolean };

  /**
   * When set, clicking an existing link opens our inline link-editor popup
   * (pre-filled, with a Remove action) instead of Quill's snow-theme tooltip.
   * Opt-in (table cells) so every other rich-text surface keeps the default
   * snow tooltip — and its built-in Remove — untouched.
   */
  linkClickOpensEditor?: boolean;

  /**
   * Custom clipboard matchers. See https://quilljs.com/docs/modules/clipboard/#clipboard-matcher
   */
  customClipboardMatchers?: readonly QuillClipboardMatcher[];
}

let modulesRegistered = false;

function registerImageLayoutImageFormat() {
  const BaseImage: any = Quill.import("formats/image");

  class ImageWithLayout extends BaseImage {
    static formats(domNode: HTMLElement) {
      const formats = super.formats(domNode) || {};
      if (!(domNode instanceof HTMLImageElement)) {
        return formats;
      }
      for (const format of IMAGE_LAYOUT_STYLE_FORMATS) {
        const value = format.readValue(domNode);
        if (format.whitelist.includes(value)) {
          formats[format.attrName] = value;
        }
      }
      return formats;
    }

    format(name: string, value: unknown) {
      const layoutFormat = IMAGE_LAYOUT_STYLE_FORMATS.find(
        (format) => format.attrName === name,
      );
      if (!layoutFormat) {
        super.format(name, value);
        return;
      }

      if (typeof value === "string" && layoutFormat.whitelist.includes(value)) {
        if (layoutFormat.keyName === "float") {
          this.domNode.style.cssFloat = value;
        } else {
          this.domNode.style.setProperty(layoutFormat.keyName, value);
        }
      } else if (layoutFormat.keyName === "float") {
        this.domNode.style.cssFloat = "";
      } else {
        this.domNode.style.removeProperty(layoutFormat.keyName);
      }
    }
  }

  Quill.register(ImageWithLayout, true);
}

/**
 * Per-selection font family, written as an inline style instead of the
 * `ql-font-<name>` classes Quill's built-in `font` format emits. The tracking
 * page drops merchant HTML straight into the shopper's page, where no
 * stylesheet defines those classes, so a class-based family would silently
 * render as the page default.
 *
 * Registered under its own `font-family` name rather than replacing `font`, so
 * the surfaces still on the class attributor keep emitting classes. The name
 * matches the CSS property on purpose: Quill's clipboard looks up pasted style
 * keys by attributor name, so this is what makes a pasted family survive.
 *
 * Deliberately carries no whitelist. The toolbar decides what a merchant can
 * pick, and a whitelist here would silently drop pasted and already-saved
 * families.
 */
function registerFontFamilyFormat() {
  class FontFamilyAttributor extends Parchment.StyleAttributor {
    override add(node: HTMLElement, value: string): boolean {
      return super.add(node, cssFontFamilyList(value));
    }

    /** Browsers re-quote as they please; normalize so values compare equal. */
    override value(node: HTMLElement): string {
      return parseCssFontFamilyList(super.value(node));
    }
  }

  Quill.register(
    {
      "formats/font-family": new FontFamilyAttributor(
        "font-family",
        "font-family",
        { scope: Parchment.Scope.INLINE },
      ),
    },
    true,
  );
}

const registerQuillModules = (
  fontFamilies: readonly string[],
  inlineFontStyles: boolean,
) => {
  if (modulesRegistered) {
    return;
  }

  // Register fonts
  const Font: any = Quill.import("formats/font");
  Font.whitelist = fontFamilies;
  Quill.register(Font, true);

  // Use inline styling for font size instead of classes
  if (inlineFontStyles) {
    const SizeStyle: any = Quill.import("attributors/style/size");
    SizeStyle.whitelist = repeat([0], 100).map((_, i) => `${i + 1}px`);
    Quill.register(SizeStyle, true);
  }

  // Outlook garbles classes, inline is safer
  const AlignStyle: any = Quill.import("attributors/style/align");
  Quill.register(AlignStyle, true);

  registerFontFamilyFormat();

  registerImageLayoutImageFormat();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  Quill.register("modules/resize", ResizeModule as any);
  Quill.register(QuillLink, true);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  Quill.register("modules/magicUrl", MagicUrl as any);

  modulesRegistered = true;
};

// quill editor v2 internally displays all lists as <ol> elements, then converts them to <ul> when we callGetSemanticHTML()
// when we plug them back into quill after saving them to the db, we need to convert back to <ol> elements with data-list="bullet" attribute
// see https://github.com/slab/quill/issues/3957
export function convertToQuillHTML(html: string) {
  // SSR safety: skip DOM manipulation in pe2e tests for web
  if (
    typeof document === "undefined" &&
    process.env.TEST_TYPE === "pe2e_test" &&
    process.env.TEST_AREA === "web"
  ) {
    return html;
  }

  const container = document.createElement("div");
  container.innerHTML = html;
  const ulElements = container.querySelectorAll("ul");
  for (const ul of ulElements) {
    const ol = document.createElement("ol");
    const liElements = ul.querySelectorAll("li");
    for (const li of liElements) {
      const newLi = document.createElement("li");
      newLi.setAttribute("data-list", "bullet");
      newLi.innerHTML = li.innerHTML;
      ol.append(newLi);
    }
    ul.replaceWith(ol);
  }
  return container.innerHTML;
}

const DEFAULT_QUILL_FONT_FAMILIES = [
  "monospace",
  "serif",
  "sans-serif",
  "arial",
  "courier-new",
  "georgia",
  "lucida-sans",
  "tahoma",
  "times-new-roman",
  "verdana",
] as const;

const QUILL_FORMATS_EXCEPT_EMBEDS = [
  "background",
  "bold",
  "color",
  "font",
  "code",
  "italic",
  "link",
  "size",
  "strike",
  "script",
  "underline",
  "blockquote",
  "header",
  "indent",
  "list",
  "align",
  "direction",
  "code-block",
];

export const QUILL_FONT_SIZES = [
  "8",
  "10",
  "11",
  "12",
  "14",
  "16",
  "20",
  "24",
  "36",
];

const DEFAULT_FONT_SIZE = 16;

/**
 * The text area alongside a quill instance that fired text changed callbacks.
 * Customize your editor's behavior by grabbing the quill instance from the ref.
 */
export const QuillEditor = forwardRef<Quill, QuillEditorProps>(
  function QuillEditor(
    {
      toolbarId,
      commonToolbar,
      readOnly,
      defaultHtmlContents,
      defaultHtmlContentOverridesInternalState,

      onTextChange,
      onSelectionChange,
      onEditorChange,
      placeholder,
      onQuillEditorEmptyChange,

      cursorIndexRef,
      setEmojiPickerOpenRef,

      inlineFontSizeStyles: inlineFontStyles = true,
      editorClassName,
      editorStyles,
      editorId,
      emojiPickerAnchorOverride,
      dynamicVariables,
      enableInlineVariableAutocomplete = false,
      enableMagicUrl = true,
      defaultLinkStyle,
      linkClickOpensEditor = false,
      allowEmbeds = true,
      fontFamilies = DEFAULT_QUILL_FONT_FAMILIES,
      customClipboardMatchers,
      schemaType,
    }: QuillEditorProps,
    ref: ForwardedRef<Quill>,
  ) {
    const defaultFontSize = editorStyles?.fontSize ?? DEFAULT_FONT_SIZE;
    const containerRef = useRef(null);
    const fakeCursorRef = useRef<HTMLDivElement>(null);

    const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
    if (setEmojiPickerOpenRef) {
      setEmojiPickerOpenRef.current = setEmojiPickerOpen;
    }
    const emojiButtonRef = useRef<HTMLButtonElement | null>(null);
    const [emojiPickerOpenedWithHotkey, setEmojiPickerOpenedWithHotkey] =
      useState(false);

    const [linkPopupOpen, setLinkPopupOpen] = useState(false);
    // Captured once when the popup opens (from the saved range) so an existing
    // link opens pre-filled — without calling getFormat() during render, which
    // would force-focus the editor and steal focus from the URL input.
    const [linkPopupInitialUrl, setLinkPopupInitialUrl] = useState("");
    const linkButtonRef = useRef<HTMLButtonElement | null>(null);
    const [linkPopupAnchor, setLinkPopupAnchor] = useState<HTMLElement | null>(
      null,
    );

    const [colorPickerOpen, setColorPickerOpen] = useState(false);
    const colorButtonRef = useRef<HTMLButtonElement | null>(null);
    const [colorPickerAnchor, setColorPickerAnchor] =
      useState<HTMLElement | null>(null);
    const [fontFamilyPickerOpen, setFontFamilyPickerOpen] = useState(false);
    const fontFamilyButtonRef = useRef<HTMLButtonElement | null>(null);
    const [fontFamilyAnchor, setFontFamilyAnchor] =
      useState<HTMLElement | null>(null);
    const [quillColor, setQuillColor] = useState<string | undefined>();
    const [quillFontFamily, setQuillFontFamily] = useState<
      string | undefined
    >();
    const [quillFontSize, setQuillFontSize] = useState<number | null>(
      defaultFontSize,
    );
    // Stable so the once-bound Quill listeners see the current default size.
    const readCurrentFontSize = useHandler((quill: Quill) =>
      readFontSize(quill, defaultFontSize),
    );

    const [dynamicVariablePickerOpen, setDynamicVariablePickerOpen] =
      useState(false);
    const [quillRoot, setQuillRoot] = useState<HTMLElement | null>(null);
    const dynamicVariableButtonRef = useRef<HTMLButtonElement | null>(null);

    const { theme } = useRequiredContext(ThemeContext);

    const quillRef = useRef<Quill | null>(null);
    const [cursorIndex, setCursorIndex] = useState(0);

    useEffect(() => {
      if (containerRef.current == null) {
        return;
      }
      const container = containerRef.current as HTMLElement;

      registerQuillModules(fontFamilies, inlineFontStyles);
      const quill = new Quill(container, {
        placeholder: readOnly ? "" : placeholder || "",
        theme: "snow",
        readOnly: !!readOnly,
        modules: {
          toolbar: !toolbarId
            ? false
            : {
                container: `#${toolbarId}`,
                handlers: {
                  "dynamic-variables"(value: any) {
                    const range = quill.getSelection();
                    if (range) {
                      quill.insertText(range.index, value);
                    }
                  },
                },
              },

          resize: { locale: {} },
          clipboard: {
            matchers: [
              quillClipboardMatchers.img,
              // The `font-family` format is registered globally, so without
              // this every surface would keep the families riding in on
              // pasted HTML. Only surfaces that offer the Font control (and
              // load the faces it names) may keep them.
              ...(commonToolbar?.fontFamilies?.length
                ? []
                : [quillClipboardMatchers.disallowFontFamily]),
              ...(customClipboardMatchers ?? []),
            ],
          },
          magicUrl: enableMagicUrl,
        },
        // Explicitly allowing everything but embeds implicitly disallows embeds.
        formats: allowEmbeds ? null : QUILL_FORMATS_EXCEPT_EMBEDS,
      });
      quillRef.current = quill;
      setQuillRoot(quill.root);
      if (cursorIndexRef) {
        cursorIndexRef.current = 0;
      }

      // Pass Quill instance to parent via forwarded ref
      if (ref) {
        if (typeof ref === "function") {
          ref(quill);
        } else {
          ref.current = quill;
        }
      }

      // Attach listeners for quill events
      const quillListeners: [
        Value<(typeof Emitter)["events"]>,
        (...args: any) => void,
      ][] = [];

      function handleSelectionChange(range: Range) {
        if (!range) {
          return;
        }
        if (cursorIndexRef) {
          cursorIndexRef.current = range.index;
        }
        setCursorIndex(range.index);
        // Extending a selection keeps the same index, so the size must be
        // re-read here rather than only when the index moves.
        setQuillFontSize(readCurrentFontSize(quill));
        try {
          // Read format across the whole selection so a selected link/colored
          // run reflects its color in the picker.
          const formats = quill.getFormat(range);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          setQuillColor(formats.color as string | undefined);
          // Mixed across the selection, Quill hands back an array; treat that
          // as "no single family" so the list marks nothing selected.
          const family = formats["font-family"];
          setQuillFontFamily(typeof family === "string" ? family : undefined);
        } catch {
          // getFormat can throw on malformed document content
        }
      }
      quillListeners.push(["selection-change", handleSelectionChange]);
      quillListeners.push(["editor-change", handleEditorChange]);

      // Clicking a link opens our link editor (pre-filled), instead of Quill's
      // snow-theme tooltip showing the raw href — which is confusing for the
      // table block's `{{ … }}` variable links. We select the whole link so the
      // popup's save reformats it in place.
      function handleLinkClick(event: MouseEvent) {
        const target = event.target;
        const anchor = target instanceof Element ? target.closest("a") : null;
        if (!anchor) {
          return;
        }
        event.preventDefault();
        const blot = Quill.find(anchor);
        if (!blot || blot instanceof Quill) {
          return;
        }
        const index = quill.getIndex(blot);
        quill.setSelection(index, blot.length());
        setLinkPopupAnchor(
          visibleToolbarAnchor(linkButtonRef.current, toolbarId),
        );
        setLinkPopupInitialUrl(anchor.getAttribute("href") ?? "");
        setLinkPopupOpen(true);
      }
      if (linkClickOpensEditor) {
        quill.root.addEventListener("click", handleLinkClick);
      }

      quillListeners.push([
        "editor-change",
        (...args) => {
          onQuillEditorEmptyChange?.(quill.getLength() <= 1);
        },
      ]);

      // Attach listeners to toolbar buttons and store refs to toolbar buttons
      const toolbarListeners: [
        HTMLElement,
        keyof HTMLElementEventMap,
        EventListenerOrEventListenerObject,
      ][] = [];
      const toolbarElement = document.querySelector(`#${toolbarId}`);

      const emojiButton = toolbarElement?.querySelector(".rql-emoji") as
        | HTMLButtonElement
        | null
        | undefined;
      if (emojiButton) {
        emojiButtonRef.current = emojiButton;
        toolbarListeners.push([emojiButton, "click", openEmojiPickerOnClick]);
      }

      const linkButton = toolbarElement?.querySelector(".rql-link") as
        | HTMLButtonElement
        | null
        | undefined;
      if (linkButton) {
        linkButtonRef.current = linkButton;
        toolbarListeners.push([linkButton, "click", openLinkPopupOnClick]);
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const colorButton = toolbarElement?.querySelector(
        ".rql-color",
      ) as HTMLButtonElement;
      if (colorButton) {
        colorButtonRef.current = colorButton;
        toolbarListeners.push([colorButton, "click", openColorPickerOnClick]);
      }

      const fontFamilyButton =
        toolbarElement?.querySelector<HTMLButtonElement>(".rql-font-family");
      if (fontFamilyButton) {
        fontFamilyButtonRef.current = fontFamilyButton;
        toolbarListeners.push([
          fontFamilyButton,
          "click",
          openFontFamilyPickerOnClick,
        ]);
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const dynamicVariableButton = toolbarElement?.querySelector(
        ".rql-dynamic-variable",
      ) as HTMLButtonElement;
      if (dynamicVariableButton) {
        dynamicVariableButtonRef.current = dynamicVariableButton;
        toolbarListeners.push([
          dynamicVariableButton,
          "click",
          openDynamicVariablePickerOnClick,
        ]);
      }

      if (defaultHtmlContents) {
        quill.setContents(
          quill.clipboard.convert({ html: defaultHtmlContents }),
        );
      }

      const pasteHandler = function (e: ClipboardEvent) {
        const clipboard = e.clipboardData;
        const pastedHTML = clipboard?.getData("text/html");
        const pastedText = clipboard?.getData("text/plain");

        if (pastedText) {
          // Magic URL doesn't work with pasted in urls for quill 2.0 so we will handle those ourselves here
          const urlRegex =
            /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/i;
          if (urlRegex.test(pastedText.trim())) {
            e.preventDefault();
            const currentSelection = quill.getSelection();
            if (currentSelection) {
              quill.deleteText(currentSelection.index, currentSelection.length);
              quill.insertText(
                currentSelection.index,
                pastedText,
                { link: pastedText },
                Quill.sources.USER,
              );
              quill.setSelection(currentSelection.index + pastedText.length, 0);
            }
          }
        }

        const selection = quill.getSelection();
        if (!selection || !pastedHTML) {
          return;
        }

        const [blot] = quill.getLeaf(selection.index);
        if (!blot) {
          return;
        }

        const tempElement = document.createElement("div");
        tempElement.innerHTML = pastedHTML;

        let contentNode = null;

        // Iterate through the nodes to find the actual content (skip meta tags)
        for (const node of tempElement.childNodes) {
          if (node.nodeName.toLowerCase() !== "meta") {
            contentNode = node;
            break;
          }
        }

        if (contentNode && shouldSeparateNode(contentNode, blot)) {
          const currentPos = selection.index;
          const currentLine = quill.getLine(currentPos);

          // Insert a new line before the pasted content if we're not at the beginning of a line
          if (currentPos > 0 && currentLine[1] > 0) {
            quill.insertText(currentPos, "\n");
          }
        }
      };
      quill.root.addEventListener("paste", pasteHandler, true);

      function shouldSeparateNode(node: Node, blot: Parchment.LeafBlot) {
        const nodeName = node.nodeName.toLowerCase();

        // Block level elements that should trigger separation
        const blockElements = [
          "p",
          "h1",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
          "div",
          "blockquote",
          "pre",
          "ul",
          "ol",
          "li",
        ];

        if (!blockElements.includes(nodeName)) {
          return false;
        }

        let parentBlot = blot.parent;

        // Navigate up the blot hierarchy to find the block-level blot
        while (
          parentBlot &&
          !(
            parentBlot.statics &&
            (parentBlot.statics.blotName === "block" ||
              blockElements.includes(
                String(parentBlot.statics.tagName).toLowerCase(),
              ))
          )
        ) {
          parentBlot = parentBlot.parent;
        }

        if (!parentBlot || !parentBlot.domNode) {
          return true;
        }

        const currentTagName = parentBlot.domNode.nodeName.toLowerCase();

        if (currentTagName !== nodeName) {
          if (currentTagName.startsWith("h") && nodeName.startsWith("h")) {
            const currentLevel = parseInt(currentTagName.substring(1), 10);
            const newLevel = parseInt(nodeName.substring(1), 10);
            return currentLevel !== newLevel;
          }

          if (
            (currentTagName === "ul" && nodeName === "ol") ||
            (currentTagName === "ol" && nodeName === "ul")
          ) {
            return true;
          }
          return true;
        }
        return false;
      }

      for (const [event, callback] of quillListeners) {
        quill.on(event, callback);
      }
      for (const [element, event, callback] of toolbarListeners) {
        element.addEventListener(event, callback);
      }
      return () => {
        for (const [event, callback] of quillListeners) {
          quill.off(event, callback);
        }
        for (const [element, event, callback] of toolbarListeners) {
          element.removeEventListener(event, callback);
        }
        quill.root.removeEventListener("paste", pasteHandler, true);
        if (linkClickOpensEditor) {
          quill.root.removeEventListener("click", handleLinkClick);
        }
        if (typeof ref === "function") {
          ref(null);
        } else if (ref) {
          ref.current = null;
        }
        container.innerHTML = "";
      };
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, readOnly, toolbarId]);

    useEffect(() => {
      if (!quillRef.current) {
        return;
      }
      setQuillFontSize(readCurrentFontSize(quillRef.current));
    }, [cursorIndex, defaultFontSize, readCurrentFontSize]);

    // use defaultHtmlContentOverridesInternalState to force quill to re-render
    // when the default html contents change
    useEffect(() => {
      if (!quillRef.current) {
        return;
      }
      if (defaultHtmlContentOverridesInternalState && defaultHtmlContents) {
        // Don't do anything if we're in the middle of user input
        const isUserTyping = document.activeElement === quillRef.current.root;
        if (isUserTyping) {
          return;
        }

        // Save current selection
        const currentRange = quillRef.current.getSelection();

        // Update content
        quillRef.current.setContents(
          quillRef.current.clipboard.convert({ html: defaultHtmlContents }),
        );

        // Restore selection if it existed
        if (currentRange) {
          setTimeout(() => {
            if (quillRef.current) {
              // Ensure the index isn't beyond the new content length
              const contentLength = quillRef.current?.getLength();
              const safeIndex = Math.min(currentRange.index, contentLength - 1);
              quillRef.current.setSelection(safeIndex, 0);
            }
          }, 0);
        }
      }
    }, [defaultHtmlContents, defaultHtmlContentOverridesInternalState]);

    // Attach onTextChange
    useEffect(() => {
      const quill = quillRef.current;
      if (!quill || !onTextChange) {
        return;
      }
      quill.on("text-change", onTextChange);
      return () => {
        quill.off("text-change", onTextChange);
      };
    }, [onTextChange]);

    // Attach onSelectionChange
    useEffect(() => {
      const quill = quillRef.current;
      if (!quill || !onSelectionChange) {
        return;
      }
      quill.on("selection-change", onSelectionChange);
      return () => {
        quill.off("selection-change", onSelectionChange);
      };
    }, [onSelectionChange]);

    // Attach onEditorChange
    useEffect(() => {
      const quill = quillRef.current;
      if (!quill || !onEditorChange) {
        return;
      }
      quill.on("editor-change", onEditorChange);
      return () => {
        quill.off("editor-change", onEditorChange);
      };
    }, [onEditorChange]);

    function openEmojiPickerOnClick(event: Event) {
      event.stopPropagation();
      setEmojiPickerOpen((emojiPickerOpen) => {
        if (!emojiPickerOpen) {
          setEmojiPickerOpenedWithHotkey(false);
        }
        return !emojiPickerOpen;
      });
    }
    function openLinkPopupOnClick() {
      setLinkPopupAnchor(
        visibleToolbarAnchor(linkButtonRef.current, toolbarId),
      );
      setLinkPopupOpen((open) => {
        if (open) {
          quillRef.current?.focus();
        } else if (!quillRef.current?.selection.savedRange?.length) {
          return false;
        } else {
          // Pre-fill with the existing link's URL (if the selection is already
          // linked). getFormat is given the saved range so it doesn't focus.
          const savedRange = quillRef.current.selection.savedRange;
          const linkFormat = quillRef.current.getFormat(savedRange).link;
          setLinkPopupInitialUrl(
            typeof linkFormat === "string" ? linkFormat : "",
          );
        }
        return !open;
      });
    }
    function openColorPickerOnClick() {
      setColorPickerAnchor(
        visibleToolbarAnchor(colorButtonRef.current, toolbarId),
      );
      setColorPickerOpen((open) => {
        if (open) {
          quillRef.current?.focus();
        }
        return !open;
      });
    }
    function openFontFamilyPickerOnClick() {
      // Resolved per click, not once: whether the control is collapsed into the
      // overflow menu changes with the toolbar's width.
      setFontFamilyAnchor(
        visibleToolbarAnchor(fontFamilyButtonRef.current, toolbarId),
      );
      setFontFamilyPickerOpen((open) => {
        if (open) {
          quillRef.current?.focus();
        }
        return !open;
      });
    }
    function openDynamicVariablePickerOnClick() {
      setDynamicVariablePickerOpen((open) => {
        if (open) {
          quillRef.current?.focus();
        }
        return !open;
      });
    }

    function handleEditorChange(...args: OnEditorChangeArgs) {
      const [name, ...rest] = args;
      if (name === Quill.events.SELECTION_CHANGE) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const range = rest[0] as Range;
        if (range?.index !== undefined) {
          if (cursorIndexRef) {
            cursorIndexRef.current = range.index;
          }
          setCursorIndex(range.index);
        }
      }
    }

    const insertTextAndUpdateCursorIndex = useCallback(
      ({ text, link }: { text: string; link?: string }) => {
        const quill = quillRef.current;
        if (!quill) {
          return;
        }
        const selection = quill.getSelection();

        if (selection && selection.length) {
          quill.deleteText(cursorIndex, selection.length);
        }
        quill.insertText(cursorIndex, text, "link", link, Quill.sources.USER);

        const newCursorIndex = cursorIndex + text.length;
        quill.setSelection(newCursorIndex, 0);
        if (cursorIndexRef) {
          cursorIndexRef.current = newCursorIndex;
        }
        setCursorIndex(newCursorIndex);
      },
      [setCursorIndex, cursorIndex, cursorIndexRef],
    );

    function handleEmojiSelected(emoji: BaseEmoji | CustomEmoji) {
      if (!("native" in emoji)) {
        return;
      } // Rule out CustomEmoji
      insertTextAndUpdateCursorIndex({ text: emoji.native });
    }

    function handleHotkey(event: React.KeyboardEvent) {
      if (!event.ctrlKey && !event.metaKey) {
        return;
      }
      if (event.key === "3") {
        setEmojiPickerOpenedWithHotkey(true);
        setEmojiPickerOpen(true);
        event.preventDefault();
      }
    }

    function closeEmojiPicker(cause: EmojiSelectionModalCloseCause) {
      setEmojiPickerOpenedWithHotkey(false);
      setEmojiPickerOpen(false);
      if (cause !== "clickOutside") {
        quillRef.current?.focus();
      }
    }

    const closeColorPicker = useCallback(() => {
      setColorPickerOpen(false);
      const quill = quillRef.current;
      if (!quill) {
        return;
      }

      if (quill.selection.savedRange.length) {
        onTextChange?.();
      }
    }, [onTextChange]);

    const handleColorPicked = useCallback((color: string) => {
      setQuillColor(color);
      const quill = quillRef.current;
      if (!quill) {
        return;
      }
      formatQuillWithoutFocusingRisky(quill, "color", color);
    }, []);

    /**
     * Formatting through the saved range rather than `quill.format` keeps the
     * caret where the merchant left it; clicking the toolbar has already moved
     * focus out of the editor by the time this runs.
     */
    const handleFontFamilyPicked = useCallback(
      (fontFamily: string) => {
        setFontFamilyPickerOpen(false);
        const quill = quillRef.current;
        if (!quill) {
          return;
        }
        formatQuillWithoutFocusingRisky(
          quill,
          "font-family",
          fontFamily || undefined,
        );
        quill.focus();
        onTextChange?.();
      },
      [onTextChange],
    );

    const handleColorPickerKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === "Escape") {
          closeColorPicker();
          quillRef.current?.focus();
          // Don't let the enter press go through to quill
          event.preventDefault();
        }
      },
      [closeColorPicker],
    );

    const handleDynamicVariableSelected = useCallback(
      (variable: DynamicVariable) => {
        if (variable.type === "text") {
          insertTextAndUpdateCursorIndex({
            text: applyDynamicVariableSyntaxLiquid({
              ...variable,
              isHtml: true,
              schemaType: schemaType ?? null,
            }),
          });
        } else if (variable.type === "url") {
          insertTextAndUpdateCursorIndex({
            text: variable.text,
            link: applyDynamicVariableSyntaxLiquid({
              ...variable,
              isHtml: true,
              schemaType: schemaType ?? null,
            }),
          });
        } else {
          assertNever(variable);
        }
      },
      [insertTextAndUpdateCursorIndex, schemaType],
    );

    // Viewport rect of the caret so the inline menu anchors exactly under
    // where the user is typing. Prefer the browser's native selection rect —
    // it is already in viewport coordinates and fully accounts for the slip
    // artboard's zoom/transform. Fall back to Quill's own bounds (corrected
    // for ancestor CSS scale) when the native rect is unavailable/empty.
    const getCaretViewportRect = useCallback((): DOMRect => {
      const quill = quillRef.current;
      const domSelection =
        typeof window !== "undefined" ? window.getSelection() : null;
      if (
        domSelection &&
        domSelection.rangeCount > 0 &&
        quill?.root.contains(domSelection.anchorNode)
      ) {
        const range = domSelection.getRangeAt(0).cloneRange();
        range.collapse(true);
        const rect = range.getClientRects()[0] ?? range.getBoundingClientRect();
        if (rect && (rect.left !== 0 || rect.top !== 0 || rect.height !== 0)) {
          return new DOMRect(rect.left, rect.top, rect.width || 1, rect.height);
        }
      }
      if (!quill) {
        return new DOMRect();
      }
      const selection = quill.getSelection();
      const index = selection ? selection.index : 0;
      const root = quill.root;
      const rootRect = root.getBoundingClientRect();
      const bounds = quill.getBounds(index);
      if (!bounds) {
        return rootRect;
      }
      const scaleX = root.offsetWidth ? rootRect.width / root.offsetWidth : 1;
      const scaleY = root.offsetHeight
        ? rootRect.height / root.offsetHeight
        : 1;
      return new DOMRect(
        rootRect.left + bounds.left * scaleX,
        rootRect.top + bounds.top * scaleY,
        Math.max(1, bounds.width * scaleX),
        bounds.height * scaleY,
      );
    }, []);

    const inline = useInlineVariableAutocomplete({
      enabled: !!enableInlineVariableAutocomplete,
      target: quillRoot,
      variables: dynamicVariables ?? [],
      textBeforeCaret: () => {
        const quill = quillRef.current;
        const selection = quill?.getSelection();
        return !quill || !selection || selection.length > 0
          ? null
          : quill.getText(0, selection.index);
      },
      replaceQuery: (startIndex, field) => {
        const quill = quillRef.current;
        const selection = quill?.getSelection();
        if (!quill) {
          return;
        }
        const caret = selection ? selection.index : startIndex;
        const token = applyDynamicVariableSyntaxLiquid({
          key: field.key,
          isHtml: true,
          schemaType: schemaType ?? null,
        });
        quill.deleteText(
          startIndex,
          Math.max(0, caret - startIndex),
          Quill.sources.USER,
        );
        quill.insertText(startIndex, token, Quill.sources.USER);
        const newCursorIndex = startIndex + token.length;
        // Selecting from the portaled menu drops editor focus, which would
        // otherwise strand the caret.
        quill.focus();
        quill.setSelection(newCursorIndex, 0);
        setCursorIndex(newCursorIndex);
        if (cursorIndexRef) {
          cursorIndexRef.current = newCursorIndex;
        }
      },
    });

    const setFontSize = useCallback(
      (size: number) => {
        const quill = quillRef.current;
        if (!quill) {
          setQuillFontSize(size);
          return;
        }
        if (applyFontSize(quill, size)) {
          onTextChange?.();
        }
        setQuillFontSize(readCurrentFontSize(quill));
      },
      [onTextChange, readCurrentFontSize],
    );

    const stepQuillFontSize = useCallback(
      (delta: 1 | -1) => {
        const quill = quillRef.current;
        if (!quill) {
          return;
        }
        if (stepFontSize(quill, delta, defaultFontSize)) {
          onTextChange?.();
        }
        setQuillFontSize(readCurrentFontSize(quill));
      },
      [onTextChange, defaultFontSize, readCurrentFontSize],
    );

    let cursorBounds: { left: number; top: number; height: number } | null =
      null;
    try {
      const b = quillRef.current?.getBounds(cursorIndex);
      if (b) {
        cursorBounds = b;
      }
    } catch {
      // getBounds can throw on malformed document content
    }

    return (
      <div className={quillEditorCss.container}>
        <div
          className={classNames(quillEditorCss.fakeCursor, {
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            [quillEditorCss.active]: emojiPickerOpen,
          })}
          ref={fakeCursorRef}
          style={{
            padding: quillFakeCursorPadding + "px",
            left: (cursorBounds?.left || 0) - quillFakeCursorPadding,
            top: (cursorBounds?.top || 0) - quillFakeCursorPadding,
            width: 1 + quillFakeCursorPadding * 2 + "px",
            height:
              (cursorBounds?.height || 0) + quillFakeCursorPadding * 2 + "px",
          }}
        />
        {commonToolbar && (
          <FormQuillEditorToolbar
            anchorEl={commonToolbar.anchorEl}
            anchorRect={commonToolbar.anchorRect}
            fontSize={quillFontSize}
            id={toolbarId}
            includeDynamicVariables={!!dynamicVariables?.length}
            includeEmoji={commonToolbar.includeEmoji}
            includeFontFamily={!!commonToolbar.fontFamilies?.length}
            includeHeading={commonToolbar.includeHeading}
            includeLink={commonToolbar.includeLink}
            inline={commonToolbar.inline}
            open={commonToolbar.toolbarOpen}
            setFontSize={setFontSize}
            stepFontSize={stepQuillFontSize}
            usePortal={commonToolbar.usePortal}
          />
        )}
        <EmojiSelectionModal
          anchor={
            emojiPickerAnchorOverride ||
            (emojiPickerOpenedWithHotkey
              ? fakeCursorRef.current
              : emojiButtonRef.current)
          }
          handleEmojiSelected={handleEmojiSelected}
          onClose={closeEmojiPicker}
          open={emojiPickerOpen}
          theme={theme}
        />
        <ClickAwayListener
          mouseEvent="onMouseDown"
          onClickAway={closeColorPicker}
          touchEvent="onTouchStart"
        >
          <Popper
            anchorEl={colorPickerAnchor}
            onKeyDown={handleColorPickerKeyDown}
            open={colorPickerOpen}
          >
            <RedoColorPicker
              onChange={handleColorPicked}
              value={quillColor ?? "#000000"}
            />
          </Popper>
        </ClickAwayListener>
        {commonToolbar?.fontFamilies?.length ? (
          <ClickAwayListener
            mouseEvent="onMouseDown"
            onClickAway={() => setFontFamilyPickerOpen(false)}
            touchEvent="onTouchStart"
          >
            <div>
              <Dropdown
                anchor={fontFamilyAnchor}
                fitToAnchor={false}
                open={fontFamilyPickerOpen}
              >
                <FontFamilyList
                  inheritLabel={
                    commonToolbar.fontFamilyInheritLabel ?? "Page default"
                  }
                  onSelect={handleFontFamilyPicked}
                  options={commonToolbar.fontFamilies}
                  value={quillFontFamily}
                />
              </Dropdown>
            </div>
          </ClickAwayListener>
        ) : null}
        {dynamicVariablePickerOpen && (
          <DynamicVariableSelectionModal
            dynamicVariables={dynamicVariables ?? []}
            handleDynamicVariableSelected={handleDynamicVariableSelected}
            onClose={() => setDynamicVariablePickerOpen(false)}
            open={dynamicVariablePickerOpen}
          />
        )}
        {inline.open && (
          <InlineVariableMenu
            activeIndex={inline.activeIndex}
            anchor={{ getBoundingClientRect: getCaretViewportRect }}
            fields={inline.fields}
            onActiveIndexChange={inline.setActiveIndex}
            onClickAway={inline.close}
            onSelect={inline.select}
          />
        )}
        {linkPopupOpen && (
          <QuillLinkPopup
            anchorEl={linkPopupAnchor}
            initialUrl={linkClickOpensEditor ? linkPopupInitialUrl : ""}
            onClose={() => setLinkPopupOpen(false)}
            onRemove={
              linkClickOpensEditor
                ? () => {
                    setLinkPopupOpen(false);
                    quillRef.current?.format("link", false);
                  }
                : undefined
            }
            onSubmit={(url) => {
              setLinkPopupOpen(false);
              const quill = quillRef.current;
              if (!quill) {
                return;
              }
              quill.format("link", sanitizeUrl(url));
              if (defaultLinkStyle?.color) {
                quill.format("color", defaultLinkStyle.color);
              }
              if (defaultLinkStyle?.underline) {
                quill.format("underline", true);
              }
            }}
          />
        )}
        <div
          className={editorClassName}
          id={editorId}
          onKeyDown={handleHotkey}
          ref={containerRef}
          style={editorStyles}
        />
      </div>
    );
  },
);

export function pasteHtmlWithoutFocusing(quill: Quill, html: string) {
  const delta = quill.clipboard.convert({ html });
  quill.setContents(delta);
}

/**
 * This function is copied from quill.format but with certain
 * things changed to avoid focusing the quill editor. In particular,
 * 1. This function bypasses quill's event system. No EDITOR_CHANGE or
 *  TEXT_CHANGE events will fire as a result of this function.
 * 2. Instead of calling quill.getSelection, this function
 *  uses a variable more internal to quill that doesn't seem to be
 *  intended for public use.
 *
 * If you aren't concerned about quill grabbing focus when you
 * format, just use quill.format.
 *
 * @param quill Quill instance.
 * @param name Name of format to change. e.g. `"color"`
 * @param value Value for format. e.g. `"#deadaf"`
 * @returns
 */
function formatQuillWithoutFocusingRisky(
  quill: Quill,
  name: string,
  value: string | undefined,
) {
  // When quill is unfocused, quill.getSelection() returns null. Instead,
  // we access the last non-null selection, stored in savedRange.
  const range = quill.selection.savedRange;
  if (range == null) {
    return;
  }
  if (quill.scroll.query(name, Parchment.Scope.BLOCK)) {
    quill.editor.formatLine(range.index, range.length, { [name]: value });
  } else if (range.length === 0) {
    quill.selection.format(name, value);
  } else {
    quill.editor.formatText(range.index, range.length, { [name]: value });
  }
}
